import config from "../../config/domain";
import GlobalStore from "../store/globalStore";
import requestMapper from "./mapper/serviceMapper";
import { encryptGetClientDataRequest } from "./encryptionService";
import { localStorageGetItem } from "./globalService";
import { trackApiError } from "./apiErrorTracker";
import { handleSessionExpired, isSessionExpired, parseResponseBody } from "./sessionExpiry";
import { prepareSecureRequest, decryptSecureResponse } from "../../secureApi/secureEncryption";

interface callReq {
  apiBaseUrl?: string;
  url: string;
  method: string;
  body?: {};
  authType?: "bearer" | "authtoken";
  appidkey?: string;
  source?: string;
  sourcetoken?: string;
  headers?: object;
  tempToken?: string | boolean;
  serviceName?: any;
  encryption?: any;
  encryptToken?: string;
  encryptedString?: boolean;
  encrypted?: boolean;
}

// Console logging of API calls: one collapsed group per call with the URL in
// the header, and the plain (pre-encryption) payload and decrypted response
// inside. Disabled in production builds so decrypted PII never reaches the
// console there.
const API_LOGGING_ENABLED = process.env.REACT_APP_ENV !== "production";

export function logApiCall(entry: {
  method: string;
  url: string;
  status?: number;
  payload?: unknown;
  response?: unknown;
  error?: unknown;
}) {
  if (!API_LOGGING_ENABLED) return;
  try {
    const failed = !!entry.error || (entry.status !== undefined && entry.status >= 400);
    const status = entry.error ? "ERROR" : entry.status ?? "";
    // %c styles only apply within the FIRST argument, so the tag lives inside
    // the format string (a separate first argument would print "%c" literally).
    console.groupCollapsed(
      `%c[SECURE_API_DEBUG]%c ${entry.method}%c ${entry.url} %c${status}`,
      "color:#6b7280",
      "font-weight:bold",
      "font-weight:normal",
      `font-weight:bold;color:${failed ? "#c62828" : "#15803d"}`
    );
    console.log("Payload:", entry.payload ?? "(none)");
    if (entry.error) console.log("Error:", entry.error);
    else console.log("Response:", entry.response);
    console.groupEnd();
  } catch {
    // logging must never affect the request flow
  }
}

export const apiService = async (req: callReq, callback: CallableFunction) => {
  const params: any = {
    method: req.method,
    headers: {
      "Content-Type": "application/json",
      "api-key": config.apiKey
    },
  };

  if (req.appidkey) {
    params.headers["appidkey"] = req.appidkey;
  }

  if (req.headers) params.headers = { ...params.headers, ...req.headers };

  // if (GlobalStore.token !== "")
  //   params.headers = { ...params.headers, mwAuth: GlobalStore.token };
  if (GlobalStore.token !== "" && req.tempToken !== false)
    params.headers = {
      ...params.headers,
      Authorization: "Bearer " + GlobalStore.token,
    };

  let clientPartialDetails = JSON.parse(
    localStorageGetItem("clientPartialDetails_ao") || "{}"
  );
  if (
    !GlobalStore.token && (GlobalStore.token == undefined || GlobalStore?.token == null || GlobalStore?.token == "") &&
    clientPartialDetails.token &&
    req.tempToken !== false
  ) {
    params.headers = {
      ...params.headers,
      Authorization: "Bearer " + clientPartialDetails.token,
    };
  }

  if (req.encryptToken) {
    params.headers = {
      ...params.headers,
      Authorization: encryptGetClientDataRequest(req.encryptToken),
    };
  }

  if (req.method === "POST" || req.method === "PUT" || req.method === "DELETE") {
    if (req?.encryption) {
      if (req?.encryptedString) {
        const tempBody = {
          request: encryptGetClientDataRequest(req.body),
        };
        params["body"] = JSON.stringify({
          encryptedString: window.btoa(JSON.stringify(tempBody)),
        });
      } else {
        params["body"] = JSON.stringify({
          request: encryptGetClientDataRequest(req.body),
        });
      }
    } else {
      params["body"] = JSON.stringify(req.body);
    }
  }

  let httpStatus: number | undefined;
  const logBase = req.apiBaseUrl ? req.apiBaseUrl : config.apiUrl;
  const logUrl = req.encrypted ? `${logBase}v1/secured/${req.url}` : logBase + req.url;

  try {
    if (req.encrypted) {
      const baseUrl = req.apiBaseUrl ? req.apiBaseUrl : config.apiUrl;
      const fullUrl = `${baseUrl}v1/secured/${req.url}`;
      // Already-encrypted payloads (req.encryption) get the secure layer on top, i.e. double-encrypted.
      const securePayload = req.encryption && params.body ? JSON.parse(params.body) : req.body;

      const { headers: secureHeaders, body: secureBody, clientKeyPair, nonce } =
        await prepareSecureRequest({
          method: req.method,
          url: fullUrl,
          serverPublicKey: config.secureApiPublicKey,
          hmacSecret: config.secureApiHmacSecret,
          plainBody: securePayload,
        });
      const resp = await fetch(fullUrl, {
        method: req.method,
        // Caller-supplied headers + Authorization (resolved above) + secure headers.
        headers: {
          "Content-Type": "application/json",
          ...((req.headers as Record<string, string>) || {}),
          ...(params.headers.Authorization ? { Authorization: params.headers.Authorization } : {}),
          ...secureHeaders
        },

        body: secureBody,
      });

      httpStatus = resp.status;
      const isResponseEncrypted = !!resp.headers.get("X-Response-Encrypted-Key");

      if (!isResponseEncrypted) {
        if (!resp.ok) {
          const errText = await resp.text().catch(() => "");
          // Session expiry comes back as a plain (unencrypted) 401 body.
          const errBody = parseResponseBody(errText);
          if (isSessionExpired(resp.status, errBody)) {
            logApiCall({ method: req.method, url: fullUrl, status: resp.status, payload: req.body, response: errBody ?? errText });
            trackApiError({ url: req.url, serviceName: req.serviceName, httpStatus, response: errBody });
            if (handleSessionExpired(errBody)) return;
          }
          console.error("[apiService] Secure API HTTP error:", fullUrl, resp.status, errText);
          throw new Error(`HTTP ${resp.status}`);
        }
        // Missing header => server didn't encrypt the response, or CORS isn't exposing it
        throw new Error(
          "SecureApi: X-Response-Encrypted-Key header not readable. Backend must send it and list it in Access-Control-Expose-Headers.",
        );
      }

      // Backend may encrypt error responses too, so decrypt regardless of HTTP status.
      const decryptedData = await decryptSecureResponse<any>({
        response: resp,
        clientKeyPair,
        expectedNonce: nonce,
      });

      logApiCall({ method: req.method, url: fullUrl, status: resp.status, payload: req.body, response: decryptedData });

      if (!resp.ok) {
        console.error("[apiService] Secure API HTTP error:", fullUrl, resp.status, decryptedData);
        trackApiError({
          url: req.url,
          serviceName: req.serviceName,
          httpStatus,
          response: decryptedData,
        });
      }

      if (isSessionExpired(resp.status, decryptedData) && handleSessionExpired(decryptedData)) return;

      if (req.serviceName && req.serviceName !== "") {
        const res = await requestMapper(decryptedData, req.serviceName);
        callback(res);
      } else {
        callback(decryptedData);
      }
      return;
    }

    let URL = req.apiBaseUrl ? req.apiBaseUrl : config.apiUrl;
    const resp = await fetch(URL + req.url, params);
    httpStatus = resp.status;
    const newText = await resp.text();
    const newResp = parseResponseBody(newText);
    logApiCall({ method: req.method, url: URL + req.url, status: resp.status, payload: req.body, response: newResp ?? newText });

    trackApiError({
      url: req.url,
      serviceName: req.serviceName,
      httpStatus,
      response: newResp,
    });
    // handle 401 response (HTTP 401 or StatusCode "-6")
    if (isSessionExpired(resp.status, newResp) && handleSessionExpired(newResp)) return;
    if (newResp === undefined) throw new Error(`Invalid JSON response (HTTP ${resp.status})`);
    if (resp.headers.get("CEPRK")) {
      const CEPRK = resp.headers.get("CEPRK");
      callback(newResp, CEPRK);
    } else {
      // callback(newResp);
      // check if revamped API call else return to normal flow
      if (req.serviceName && req.serviceName !== "") {
        let res = await requestMapper(newResp, req.serviceName);
        callback(res);
      } else {
        callback(newResp);
      }
      // add a mapper here so that no code needs to be changed
    }

    // newResp
    //     // .json()
    //     .then((res:any) => {
    //         // handleSessionTimeout(newResp, res);
    //         callback(res, newResp);
    //     })
    //     .catch((res: any) => {
    //         // handleSessionTimeout(newResp, res);
    //         callback(undefined, newResp);
    //     });
  } catch (err) {
    // all errors will be captured here for anything in the try block

    logApiCall({ method: req.method, url: logUrl, status: httpStatus, payload: req.body, error: err });
    console.error("[apiService] Error for URL:", req.url, err);
    trackApiError({
      url: req.url,
      serviceName: req.serviceName,
      httpStatus,
      errorType: "network",
      message: (err as Error)?.message || String(err),
    });
    callback &&
      callback({
        error: "",
        errMsg: err,
      });
  }
};
